import { z } from 'zod';
import { idnoSchema, idnpSchema } from './domain';

// caiet UC61-02/03: a Specific Observer type is distinguished by the NAME of its
// role and the system imposes no limit on how many types exist. The observer
// family is therefore open — the System Administrator mints the role in MPass and
// defines it in analytics.observer_role_definition — while the core roles that
// drive authorisation stay a closed list the compiler still checks.
export const CORE_ROLES = ['SYSTEM_ADMIN', 'REGISTRAR_ADMIN', 'REGISTRAR_OPERATOR'] as const;
export const coreRoleSchema = z.enum(CORE_ROLES);
export type CoreRole = z.infer<typeof coreRoleSchema>;

// The shape an observer role code must have, and the ONLY copy of the rule
// (constitution SEC-17): the analytics ObserverRoleService enforces it on write
// and both System Admin forms validate against it by importing this constant, so
// a code the admin cannot create is a code a session cannot carry. It is a
// security boundary, not a form hint — isObserverRole decides who is treated as
// an observer everywhere, the Grafana proxy included — so it is anchored at both
// ends and admits nothing outside [A-Z0-9_].
//
// The word OBSERVER, then any number of `_`-delimited non-empty [A-Z0-9]
// segments. The suffix used to be a single run of 2-20 characters, which refused
// OBSERVER_MIN_SAN and OBSERVER_AGENTIA_MEDICAMENT: ordinary institution codes,
// since caiet UC61-02/03 mints these names in MPass, outside SIMSM, and caps
// neither their number nor their shape. Admitting one then cost a code change in
// four files plus a redeploy — the per-role manual step spec 001 FR-008 forbids.
// A single-character segment is admitted for the same reason: the boundary that
// matters is the anchored OBSERVER prefix and its `_` delimiter, which no
// non-observer name can cross, and a length floor on a segment only re-creates
// that redeploy (OBSERVER_MS_1). The 40-character ceiling is the width of
// analytics.observer_role_definition.role_code, so a code this admits always fits
// the column that stores it, and an arbitrary SAML string fails in one step.
export const OBSERVER_ROLE_PATTERN = /^(?=.{1,40}$)OBSERVER(?:_[A-Z0-9]+)*$/;

export type ObserverRole = 'OBSERVER' | `OBSERVER_${string}`;
export type Role = CoreRole | ObserverRole;

export function isCoreRole(value: string): value is CoreRole {
  return (CORE_ROLES as readonly string[]).includes(value);
}

export function isObserverRole(value: string): value is ObserverRole {
  return OBSERVER_ROLE_PATTERN.test(value);
}

export function isRole(value: string): value is Role {
  return isCoreRole(value) || isObserverRole(value);
}

// Does the holder of `role` satisfy an access grant addressed to `grant`?
//
// Every grant is satisfied by its own role code. On top of that the caiet's role
// table makes the Specific Observer "prin definiție și Observator general"
// (gloss: a Specific Observer is by definition also a general Observer, caiet
// UC61-01/02), so a grant addressed to the whole observer family reaches every
// OBSERVER_* code, including the ones the System Administrator mints in MPass
// after this build shipped. The relation is one-way: a grant naming one
// institution stays with that institution, and OBSERVER alone does not satisfy
// it. No other role inherits anything — a SYSTEM_ADMIN reaching a dashboard does
// so because it administers dashboards, not because a grant let it in.
export function roleSatisfiesGrant(role: string, grant: string): boolean {
  if (role === grant) {
    return true;
  }
  return grant === 'OBSERVER' && isObserverRole(role);
}

// Shape validity only. Whether an observer role is *defined in SIMSM* is a
// separate, authoritative check against observer_role_definition (bff login).
export const roleSchema: z.ZodType<Role> = z.custom<Role>(
  (value) => typeof value === 'string' && isRole(value),
  { message: 'Not a SIMSM role code' },
);

// What one role of the session grants (caiet UC10-02, UC10-04). MPass names a
// role's associations in an attribute named after the role, so two roles of one
// person can point at different Registrars; the session keeps them apart and a
// role switch changes which one is in force (docs/shape-ops-login-scopes.md §2).
export const sessionScopeSchema = z.object({
  // Active Registrars only: an unknown or inactive one is dropped at login
  // (caiet UC10-06, CF-28).
  registrarScope: z.array(idnoSchema),
  // caiet UC10-02: mapping a person onto the Operator/Administrator Registrator
  // IMSP variant requires the "tip Registrator" they are attached to, so the BFF
  // resolves it from catalog at session creation and the session carries it. The
  // distinct kinds of the active Registrars in registrarScope, not a
  // per-IDNO alignment: whether one of them is enough is each caller's question.
  // Free-form codes, because the enum belongs to catalog-service.
  registrarKinds: z.array(z.string().min(1)),
  // Owner decision D5 (docs/decizii-paritate-wireframe-2026-09-05.md): the
  // display name of each Registrar in registrarScope, index-aligned with it
  // — registrarNames[i] names registrarScope[i]. An empty string is a
  // Registrar catalog holds without a name; the screen falls back to the IDNO.
  registrarNames: z.array(z.string()),
  // The `catalog.site` ids the person may report for and read — NOT the MPass
  // organisation codes the assertion carries. caiet §Roluri gives a
  // REGISTRAR_OPERATOR the codes of their locations (`{IDNO}0001`, …); the bff
  // resolves those against catalog.site.internal_code at session creation
  // (apps/bff/src/auth/auth.service.ts), because every reader of this field
  // compares it against core.stock.site_id, three of them with no shape check.
  // Stated as a uuid here so the two vocabularies cannot be confused again.
  // Only a REGISTRAR_OPERATOR scope names locations.
  locationScope: z.array(z.string().uuid()),
});
export type SessionScope = z.infer<typeof sessionScopeSchema>;

// The flat scope fields are the ACTIVE role's scope, `scopes[activeRole]`,
// projected by the bff on every read; `scopes` holds one entry per available
// role.
export const sessionUserSchema = z
  .object({
    idnp: idnpSchema,
    firstName: z.string().min(1).max(80),
    lastName: z.string().min(1).max(80),
    activeRole: roleSchema,
    availableRoles: z.array(roleSchema).min(1),
    scopes: z.record(z.string(), sessionScopeSchema).optional(),
    // Register UC11-05, CF-27: the Registrar the session acts for, kept by the
    // server and changed only by the explicit selection; null when the active
    // role holds none, or several and none is selected yet.
    activeRegistrar: idnoSchema.nullable().optional(),
    expiresAt: z.string().datetime(),
  })
  .merge(sessionScopeSchema.partial());
export type SessionUser = z.infer<typeof sessionUserSchema>;

// caiet UC10-06, CF-28: why MPass login was refused, in the order the refusal
// screen states the causes. The bff journals the association causes under
// these names and sends each cause to `/auth/no-role` as a `reason` parameter
// (docs/shape-ops-login-refusal-reason.md §2).
export const LOGIN_REFUSAL_CAUSES = [
  'NO_KNOWN_ROLE',
  'REGISTRAR_INACTIVE',
  'REGISTRAR_UNKNOWN',
  'LOCATION_INACTIVE',
  'LOCATION_UNKNOWN',
  'CODE_MALFORMED',
  'NO_ASSOCIATION',
] as const;
export type LoginRefusalCause = (typeof LOGIN_REFUSAL_CAUSES)[number];
// The causes that name one association MPass sent.
export type AssociationDropReason = Exclude<LoginRefusalCause, 'NO_KNOWN_ROLE' | 'NO_ASSOCIATION'>;

export const clientCredentialJwtClaimsSchema = z.object({
  sub: z.string().min(1),
  iss: z.literal('simsm-auth'),
  aud: z.literal('simsm-api'),
  ip: z.string().min(7).max(45),
  iat: z.number(),
  exp: z.number(),
  jti: z.string().uuid(),
});
export type ClientCredentialJwtClaims = z.infer<typeof clientCredentialJwtClaimsSchema>;

// caiet SEC-08, UC12-02: the front office sends this header on a request no
// input of the person's caused (a badge or a view re-reading on its timer), and
// the bff does not count that request as activity: it moves neither the
// session's inactivity expiry nor the cookie's (docs/shape-ops-session-inactivity.md §4).
export const SESSION_BACKGROUND_HEADER = 'x-simsm-background';
